import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./QrPublicGallery.css";

const STORAGE_KEY = "qrp_session_";
const SESSION_EXPIRY_MS = 8 * 60 * 60 * 1000; // 8 hours

const saveSession = (token, data) => {
  try {
    const existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY + token) || "{}");
    sessionStorage.setItem(STORAGE_KEY + token, JSON.stringify({ ...existing, ...data, savedAt: Date.now() }));
  } catch { /* quota */ }
};

const loadSession = (token) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY + token);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - (data.savedAt || 0) > SESSION_EXPIRY_MS) {
      sessionStorage.removeItem(STORAGE_KEY + token);
      return null;
    }
    return data;
  } catch { return null; }
};

function QrPublicGallery() {
  const { token, resultToken } = useParams();
  const [isWhatsappMode, setIsWhatsappMode] = useState(false);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Contact fields (only for WhatsApp mode)
  const [guestName, setGuestName] = useState("");
  const [guestWhatsapp, setGuestWhatsapp] = useState("");

  // Selfie / face scan state — restored from sessionStorage on mount
  const [scanImage, setScanImage] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const cameraInputRef = useRef(null);

  // WhatsApp registration state (whatsapp mode only)
  // null = not submitted, "queued" = job accepted, then "completed" | "rejected" | "failed"
  const [waStatus, setWaStatus] = useState(null);
  const [waMatchCount, setWaMatchCount] = useState(0);
  const [waPreviewLink, setWaPreviewLink] = useState(null);
  const [waMessage, setWaMessage] = useState("");
  const waPollRef = useRef(null);
  const [resultsLoading, setResultsLoading] = useState(!!resultToken);

  // Matched photos
  const [matches, setMatches] = useState(null);
  // Matches the server found but did not sign URLs for yet: { id, similarity,
  // match_confidence }. Exchanged for URLs a page at a time via /match-urls, so
  // a guest matching hundreds of photos is not made to wait for hundreds of
  // signed URLs before seeing the first one.
  const [moreMatches, setMoreMatches] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedDownloads, setSelectedDownloads] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newPhotosCount, setNewPhotosCount] = useState(null);

  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const lightboxTouchRef = useRef(null);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}`);
      const data = await res.json();
      if (res.ok) {
        setGallery(data.gallery);
        setIsWhatsappMode(data.gallery?.public_mode === "whatsapp");
      }
      else setError(data.message || "Gallery not found");
    } catch { setError("Failed to load gallery"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  // Restore selfie + results from sessionStorage (survives tab kill, expires after 1 hour)
  useEffect(() => {
    const saved = loadSession(token);
    if (!saved) return;
    if (saved.selfie) {
      setScanImage(saved.selfie);
      setScanPreview(saved.selfie);
    }
    if (saved.matches && saved.matches.length > 0 && !resultToken) {
      setMatches(saved.matches);
      setMoreMatches(saved.moreMatches || []);
      setSelectedDownloads(new Set(saved.matches.map(m => m.id)));
      setSuccess(`Welcome back! Your ${saved.matches.length} photo${saved.matches.length > 1 ? "s are" : " is"} still here.`);
      setRestoredFromCache(true);
    }
  }, [token, resultToken]);

  useEffect(() => {
    if (!resultToken) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/qr/${token}/results/${resultToken}`);
        const data = await res.json();
        if (res.ok) {
          setMatches(data.matches || []);
          setSelectedDownloads(new Set((data.matches || []).map((m) => m.id)));
          setSuccess(data.matches?.length ? `Found ${data.matches.length} photo${data.matches.length > 1 ? "s" : ""} of you!` : "No matching photos found.");
        } else {
          setError(data.message || "Result link not found or expired");
        }
      } catch {
        setError("Failed to load your photos");
      } finally {
        setResultsLoading(false);
      }
    })();
  }, [token, resultToken]);

  // File handlers
  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
    e.target.value = "";
  };

  const processFile = (file) => {
    if (!file.type.startsWith("image/")) { setError("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setScanImage(dataUrl);
      setScanPreview(dataUrl);
      // Compress to a smaller JPEG for sessionStorage (quota ~5MB)
      const img = new Image();
      img.onload = () => {
        const maxDim = 480;
        let w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL("image/jpeg", 0.7);
        saveSession(token, { selfie: compressed });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleCameraCapture = (e) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
    e.target.value = "";
  };

  const handleFaceSearch = async () => {
    if (!scanImage) { setError("Please upload a face photo first"); return; }
    if (isWhatsappMode && (!guestName.trim() || !guestWhatsapp.trim())) {
      setError("Please enter your name and WhatsApp number");
      return;
    }
    setScanning(true); setError(""); setSuccess("");

    if (isWhatsappMode) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/qr/${token}/whatsapp-register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: guestName.trim(), whatsapp: guestWhatsapp.trim(), imageData: scanImage }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.message || "Could not process your photo"); }
        else {
          setWaStatus("queued");
          setSuccess(data.message || "We're finding your photos — you'll get a WhatsApp message shortly.");
          pollWaStatus(data.requestId);
        }
      } catch (err) { setError("Request failed: " + err.message); }
      finally { setScanning(false); }
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/search-face`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: scanImage }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Face search failed"); }
      else if (data.matches && data.matches.length > 0) {
        // matchesFound is the real total; data.matches is only the first page.
        // Reporting data.matches.length here is what told a guest in 448 photos
        // that they were in 100.
        const total = data.matchesFound ?? data.matches.length;
        setMatches(data.matches);
        setMoreMatches(data.moreMatches || []);
        setSuccess(`Found ${total} photo${total > 1 ? "s" : ""} of you!`);
        setSelectedDownloads(new Set(data.matches.map(m => m.id)));
        saveSession(token, { matches: data.matches, moreMatches: data.moreMatches || [] });
      } else {
        setMatches([]);
        setSuccess("No matching photos found. Try a clearer face photo.");
      }
    } catch (err) { setError("Face search failed: " + err.message); }
    finally { setScanning(false); }
  };

  // Fetch signed URLs for the next batch of already-matched photos. No new face
  // search happens: the server scored these during the original search, so this
  // only exchanges ids for URLs.
  const loadMoreMatches = async () => {
    if (loadingMore || moreMatches.length === 0) return;
    setLoadingMore(true);
    const batch = moreMatches.slice(0, 100);
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/match-urls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: batch.map((m) => m.id) }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Could not load more photos"); return; }

      const scoreById = new Map(batch.map((m) => [m.id, m]));
      const added = (data.matches || []).map((m) => ({ ...scoreById.get(m.id), ...m }));
      const nextMatches = [...matches, ...added];
      const nextMore = moreMatches.slice(batch.length);
      setMatches(nextMatches);
      setMoreMatches(nextMore);
      setSelectedDownloads((prev) => new Set([...prev, ...added.map((m) => m.id)]));
      saveSession(token, { matches: nextMatches, moreMatches: nextMore });
    } catch (err) {
      setError("Could not load more photos: " + err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  // Polls the queued WhatsApp registration job until it leaves "pending"/
  // "processing" — the actual work runs in the background worker, this just
  // reflects that state back into the UI so the guest sees progress without
  // needing to leave for WhatsApp.
  const pollWaStatus = (requestId, attempt = 0) => {
    const MAX_ATTEMPTS = 40; // ~2 minutes at 3s intervals
    if (waPollRef.current) clearTimeout(waPollRef.current);
    waPollRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/qr/${token}/whatsapp-register/${requestId}`);
        const data = await res.json();
        if (res.ok) {
          if (data.status === "completed") {
            setWaStatus("completed");
            setWaMatchCount(data.matchesFound || 0);
            setWaPreviewLink(data.whatsappSent ? null : data.resultLink || null);
            setSuccess(data.matchesFound > 0
              ? `Found ${data.matchesFound} photo${data.matchesFound > 1 ? "s" : ""} of you! Check your WhatsApp for the link.`
              : "No matching photos found this time. Check your WhatsApp for the link to try again.");
            return;
          }
          if (data.status === "rejected") {
            setWaStatus("rejected");
            setWaMessage(data.message || "Your selfie didn't pass our quality check.");
            setSuccess("We've sent you a WhatsApp message with tips to try again.");
            return;
          }
          if (data.status === "failed") {
            setWaStatus("failed");
            setError("Something went wrong processing your photo. Please try again.");
            return;
          }
        }
        if (attempt < MAX_ATTEMPTS) pollWaStatus(requestId, attempt + 1);
        else setSuccess("Still working on it — you'll get a WhatsApp message with your photos link once it's ready.");
      } catch {
        if (attempt < MAX_ATTEMPTS) pollWaStatus(requestId, attempt + 1);
      }
    }, 3000);
  };

  useEffect(() => () => { if (waPollRef.current) clearTimeout(waPollRef.current); }, []);

  const resetSearch = () => {
    if (waPollRef.current) { clearTimeout(waPollRef.current); waPollRef.current = null; }
    setMatches(null); setMoreMatches([]); setScanImage(null); setScanPreview(null); setSelectedDownloads(new Set());
    setWaStatus(null); setWaMatchCount(0); setWaPreviewLink(null); setWaMessage(""); setGuestName(""); setGuestWhatsapp(""); setRestoredFromCache(false);
    setNewPhotosCount(null);
    try { sessionStorage.removeItem(STORAGE_KEY + token); } catch { /* ignore */ }
  };

  const handleRefreshSearch = async () => {
    const selfie = scanImage || loadSession(token)?.selfie;
    if (!selfie) { resetSearch(); return; }
    setRefreshing(true); setError(""); setNewPhotosCount(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/search-face`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: selfie }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Search failed"); return; }
      const fresh = data.matches || [];
      const oldIds = new Set((matches || []).map(m => m.id));
      const newOnes = fresh.filter(m => !oldIds.has(m.id));
      setMatches(fresh);
      setSelectedDownloads(new Set(fresh.map(m => m.id)));
      saveSession(token, { matches: fresh });
      setRestoredFromCache(false);
      if (newOnes.length > 0) {
        setNewPhotosCount(newOnes.length);
        setSuccess(`Found ${newOnes.length} new photo${newOnes.length > 1 ? "s" : ""} of you!`);
      } else {
        setNewPhotosCount(0);
        setSuccess("No new photos yet — check back in a bit!");
      }
    } catch (err) { setError("Search failed: " + err.message); }
    finally { setRefreshing(false); }
  };

  // Download
  const toggleDownload = (id) => {
    setSelectedDownloads(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAllDownloads = () => {
    if (!matches) return;
    if (selectedDownloads.size === matches.length) setSelectedDownloads(new Set());
    else setSelectedDownloads(new Set(matches.map(m => m.id)));
  };

  const handleDownload = async () => {
    if (selectedDownloads.size === 0) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageIds: Array.from(selectedDownloads) }),
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = selectedDownloads.size === 1 ? "photo.jpg" : "photos.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("Download started!");
    } catch (err) { setError("Download failed: " + err.message); }
    finally { setDownloading(false); }
  };

  const downloadSinglePhoto = async (img) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageIds: [img.id] }),
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = img.original_filename || `photo-${img.id}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError("Download failed — please try again"); }
  };

  // Lightbox
  const displayImages = matches || [];
  const lightboxImage = lightboxIndex >= 0 ? displayImages[lightboxIndex] : null;
  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(-1);
  const goPrev = () => setLightboxIndex(i => (i > 0 ? i - 1 : displayImages.length - 1));
  const goNext = () => setLightboxIndex(i => (i < displayImages.length - 1 ? i + 1 : 0));

  const handleLbTouchStart = (e) => { lightboxTouchRef.current = e.touches[0].clientX; };
  const handleLbTouchEnd = (e) => {
    if (lightboxTouchRef.current === null) return;
    const diff = e.changedTouches[0].clientX - lightboxTouchRef.current;
    if (diff > 60) goPrev(); else if (diff < -60) goNext();
    lightboxTouchRef.current = null;
  };

  useEffect(() => {
    if (lightboxIndex < 0) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, displayImages.length]);

  if (loading || resultsLoading) return <div className="qrp-loading"><CameraLoader text="Loading..." /></div>;
  if (error && !gallery) return <div className="qrp-error-page"><h1>😕 Oops!</h1><p>{error}</p></div>;

  return (
    <div className="qrp-container">
      {/* Header */}
      <header className="qrp-header">
        <div className="qrp-header-content">
          {gallery?.logo_url && <img src={gallery.logo_url} alt="Studio" className="qrp-logo" />}
          <div>
            <h1 className="qrp-title">{gallery?.name}</h1>
            <p className="qrp-subtitle">
              {gallery?.studio_name && <span>{gallery.studio_name} • </span>}
              {gallery?.event_type && <span>{gallery.event_type} • </span>}
              {gallery?.place && <span>{gallery.place} • </span>}
              {gallery?.event_date && new Date(gallery.event_date).toLocaleDateString()}
            </p>
          </div>
        </div>
        {(matches || waStatus) && !resultToken && (
          <button className="qrp-reset-btn" onClick={resetSearch}>🔄 New Search</button>
        )}
      </header>

      {/* Messages */}
      {error && <div className="qrp-error">{error}</div>}
      {success && <div className="qrp-success">{success}</div>}

      {/* WhatsApp registration confirmation */}
      {waStatus && (
        <div className="qrp-selfie-section">
          <div className="qrp-selfie-card qrp-wa-confirm">
            {waStatus === "queued" && (
              <>
                <h2>⏳ We're on it</h2>
                <p>Your photo is in the queue — we're searching for your photos now. You'll get a WhatsApp message at {guestWhatsapp} with the link as soon as it's ready.</p>
              </>
            )}
            {waStatus === "completed" && (
              <>
                <h2>✅ Done!</h2>
                <p>
                  {waMatchCount > 0
                    ? `We found ${waMatchCount} photo${waMatchCount > 1 ? "s" : ""} of you. We've sent the link to ${guestWhatsapp} on WhatsApp.`
                    : `We couldn't find any photos of you this time. We've sent a link to ${guestWhatsapp} on WhatsApp so you can try again later.`}
                </p>
              </>
            )}
            {waStatus === "rejected" && (
              <>
                <h2>🤔 Needs a retry</h2>
                <p>{waMessage} We've sent tips to {guestWhatsapp} on WhatsApp — you can also try again right here.</p>
              </>
            )}
            {waStatus === "failed" && (
              <>
                <h2>😕 Something went wrong</h2>
                <p>We hit an issue processing your photo. Please try again.</p>
              </>
            )}
            {waPreviewLink && (
              <p className="qrp-wa-preview">
                WhatsApp isn't configured yet, so here's your link directly:{" "}
                <a href={waPreviewLink} target="_blank" rel="noopener noreferrer">{waPreviewLink}</a>
              </p>
            )}
            <button className="qrp-retry-btn" onClick={resetSearch}>Search Again</button>
          </div>
        </div>
      )}

      {/* Selfie Upload (shown when no matches yet) */}
      {matches === null && !waStatus && (
        <div className="qrp-selfie-section">
          <div className="qrp-selfie-card">
            <h2>📸 Find Your Photos</h2>
            <p>Upload a selfie to find all photos you appear in.</p>
            {isWhatsappMode && (
              <div className="qrp-contact-fields">
                <input
                  type="text"
                  className="qrp-contact-input"
                  placeholder="Your name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                />
                <input
                  type="tel"
                  className="qrp-contact-input"
                  placeholder="WhatsApp number"
                  value={guestWhatsapp}
                  onChange={(e) => setGuestWhatsapp(e.target.value)}
                />
              </div>
            )}
            <div className="qrp-tips">
              <p><strong>Tips for best results:</strong></p>
              <ul>
                <li>Use a front-facing selfie with good lighting</li>
                <li>Keep your face centered and clearly visible</li>
                <li>Avoid sunglasses, masks or heavy shadows</li>
              </ul>
            </div>

            <div
              className={`qrp-drop-area ${dragActive ? "active" : ""}`}
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            >
              <input type="file" accept="image/*" onChange={handleFileChange} className="qrp-file-input" />
              {scanPreview ? (
                <img src={scanPreview} alt="Your selfie" className="qrp-selfie-preview" />
              ) : (
                <div className="qrp-drop-placeholder">
                  <span className="qrp-drop-icon">📷</span>
                  <p>Drop your photo here or tap to browse</p>
                </div>
              )}
            </div>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="user"
              onChange={handleCameraCapture}
              style={{ display: "none" }}
            />

            <div className="qrp-selfie-actions">
              <button className="qrp-camera-btn" onClick={() => cameraInputRef.current?.click()} type="button">
                Open Camera
              </button>
              <button className="qrp-search-btn" onClick={handleFaceSearch} disabled={!scanImage || scanning}>
                {scanning ? "Scanning..." : "Find My Photos"}
              </button>
            </div>
            {scanPreview && (
              <div className="qrp-session-note">
                <span className="qrp-session-note-icon">&#10024;</span>
                <p>Your selfie is saved for this session. Feel free to switch apps, minimize, or come back later — we'll keep it ready for you for up to 8 hours.</p>
              </div>
            )}
          </div>

          <div className="qrp-info-note">
            <p>📷 <strong>{gallery?.image_count || 0}</strong> photos in this gallery</p>
            <p>Your face photo is only used for matching and is not stored.</p>
          </div>
        </div>
      )}

      {/* Matched Photos */}
      {matches !== null && (
        <div className="qrp-results-section">
          {restoredFromCache && matches.length > 0 && (
            <div className="qrp-restored-note">
              <span className="qrp-restored-note-icon">&#9889;</span>
              <p>Welcome back! We saved your results so you don't have to search again. They'll stay available for up to 8 hours.</p>
            </div>
          )}
          {matches.length === 0 ? (
            <div className="qrp-no-matches">
              <p>No matching photos found.</p>
              <p>Try uploading a clearer face photo with good lighting.</p>
              <button className="qrp-retry-btn" onClick={resetSearch}>Try Again</button>
            </div>
          ) : (
            <>
              <div className="qrp-results-bar">
                <span className="qrp-results-count">
                  Found {matches.length + moreMatches.length} photo{matches.length + moreMatches.length > 1 ? "s" : ""}
                  {moreMatches.length > 0 ? ` (showing ${matches.length})` : ""}
                </span>
                <div className="qrp-download-controls">
                  <button className="qrp-select-all-btn" onClick={selectAllDownloads}>
                    {selectedDownloads.size === matches.length ? "Deselect All" : "Select All"}
                  </button>
                  <button className="qrp-download-btn" onClick={handleDownload} disabled={selectedDownloads.size === 0 || downloading}>
                    {downloading ? "Downloading..." : `📥 Download ${selectedDownloads.size} Photo${selectedDownloads.size > 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>

              <div className="qrp-gallery">
                {matches.map((img, index) => (
                  <div key={img.id} className={`qrp-card ${selectedDownloads.has(img.id) ? "selected" : ""}`}>
                    <div className="qrp-card-img-wrapper" onClick={() => openLightbox(index)}>
                      <img src={img.thumbnail_url || img.image_url} alt={`Photo ${index + 1}`} className="qrp-card-img" loading="lazy" onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.alt = 'Image unavailable'; e.target.style.background = '#f0f0f0'; }} />
                    </div>
                    <button className="qrp-card-download" onClick={(e) => { e.stopPropagation(); downloadSinglePhoto(img); }} title="Download this photo">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    <div className="qrp-card-check" onClick={() => toggleDownload(img.id)}>
                      <input type="checkbox" checked={selectedDownloads.has(img.id)} readOnly />
                    </div>
                    {gallery.photo_frames_enabled && (
                      <button
                        className="qrp-card-frame-btn"
                        onClick={(e) => { e.stopPropagation(); window.open(`/frame-order/${token}/${img.id}`, "_blank", "noopener,noreferrer"); }}
                      >
                        Make into a Photo Frame
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {moreMatches.length > 0 && (
                <div className="qrp-load-more">
                  <button className="qrp-load-more-btn" onClick={loadMoreMatches} disabled={loadingMore}>
                    {loadingMore
                      ? "Loading..."
                      : `Load ${Math.min(100, moreMatches.length)} more photo${Math.min(100, moreMatches.length) > 1 ? "s" : ""}`}
                  </button>
                  <p className="qrp-load-more-note">{moreMatches.length} more photo{moreMatches.length > 1 ? "s" : ""} of you still to load</p>
                </div>
              )}

              <div className="qrp-refresh-banner">
                <div className="qrp-refresh-text">
                  <span className="qrp-refresh-icon">&#128248;</span>
                  <div>
                    <p className="qrp-refresh-title">More clicks happening right now!</p>
                    <p className="qrp-refresh-desc">The photographer is still capturing moments. Tap below to check if any new photos of you have arrived.</p>
                  </div>
                </div>
                <button className="qrp-refresh-btn" onClick={handleRefreshSearch} disabled={refreshing}>
                  {refreshing ? "Checking..." : "Check for New Photos"}
                </button>
                {newPhotosCount !== null && newPhotosCount > 0 && (
                  <p className="qrp-refresh-result-new">{newPhotosCount} new photo{newPhotosCount > 1 ? "s" : ""} found and added above!</p>
                )}
                {newPhotosCount === 0 && (
                  <p className="qrp-refresh-result-none">No new photos yet — the photographer is still at it, check back soon!</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="qrp-lightbox" onClick={closeLightbox} onTouchStart={handleLbTouchStart} onTouchEnd={handleLbTouchEnd}>
          <button className="qrp-lb-arrow qrp-lb-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }}>‹</button>
          <div className="qrp-lb-content" onClick={(e) => e.stopPropagation()}>
            <button className="qrp-lb-close" onClick={closeLightbox}>✕</button>
            <img src={lightboxImage.image_url} alt="Enlarged" className="qrp-lb-image" />
            <div className="qrp-lb-bottom">
              <span>{lightboxIndex + 1} / {displayImages.length}</span>
              <div className="qrp-lb-actions">
                <button
                  className="qrp-lb-download-btn"
                  onClick={(e) => { e.stopPropagation(); downloadSinglePhoto(lightboxImage); }}
                >
                  Download
                </button>
                {gallery.photo_frames_enabled && (
                  <button
                    className="qrp-lb-frame-btn"
                    onClick={(e) => { e.stopPropagation(); window.open(`/frame-order/${token}/${lightboxImage.id}`, "_blank", "noopener,noreferrer"); }}
                  >
                    Make into a Photo Frame
                  </button>
                )}
              </div>
            </div>
          </div>
          <button className="qrp-lb-arrow qrp-lb-next" onClick={(e) => { e.stopPropagation(); goNext(); }}>›</button>
        </div>
      )}
    </div>
  );
}

export default QrPublicGallery;
